import { useCallback, useEffect, useState } from 'react';

/**
 * The developer panel's open state, and the key that reveals it.
 *
 * OPUS-1637 took the settings cog off the Copilot window, which also took away
 * the preview, the JSON viewer and the rest of the developer surface. They come
 * back behind a key combination instead: nothing on screen leads to them, so an
 * operator cannot land on them mid-match by mouse.
 *
 * Ctrl+Alt+D is clear of everything the window already answers to — the numpad
 * keys that fire actions, and every F-key the grid tiles claim.
 */
export const DEVELOPER_KEY = { ctrl: true, alt: true, key: 'd' };

const isDeveloperKey = (e) =>
  e.ctrlKey && e.altKey && !e.shiftKey && String(e.key).toLowerCase() === DEVELOPER_KEY.key;

export const useDeveloperPanel = () => {
  const [open, setOpen] = useState(false);
  /*
   * Armed once the key has been pressed, and stays armed for the session.
   * uiSettings are shared with drive mode and seeded with showPreview: true,
   * so without this an operator who never asked for any of it would find a
   * preview floating over the window OPUS-1637 stripped it out of. Nothing
   * developer-facing renders until someone asks for it by name; a reload puts
   * it back to the operator's view.
   */
  const [armed, setArmed] = useState(false);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    /*
     * Capturing, so Escape closes the panel without also closing the dialog
     * that listens for it. The combination itself is captured for the same
     * reason: nothing else should see it.
     */
    const onKeyDown = (e) => {
      if (isDeveloperKey(e)) {
        e.preventDefault();
        e.stopPropagation();
        setArmed(true);
        setOpen((current) => !current);
        return;
      }
      if (open && e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open]);

  return { open, armed, close };
};
